/**
 * @module MeineAufgaben
 * @description Persoenliches Task-Management ("Meine Aufgaben") mit 3 Tabs:
 *   - Inbox (zugewiesene Tasks, noch nicht angenommen)
 *   - Alle Aufgaben (universelle Liste mit Filtern: Delegation, Faelligkeit, Suche)
 *   - Kanban (Board nach Kategorie + Stages, Drag & Drop)
 *   KPI-Bar mit 5 Cards. Task-Detail Slide-Over mit Chatter + Watchers.
 * @route /meine-aufgaben
 * @since v2.324.0
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import UniversalChatter from '../components/UniversalChatter';
import ConfirmDialog from '../components/ConfirmDialog';
import TaskDetailPanel from '../components/TaskDetailPanel';
import {
  Inbox, Sun, Calendar, CheckCircle2, Plus, Trash2, Clock,
  MessageSquare, Bot, Link as LinkIcon, X, AlertCircle,
  Eye, Users, Send as SendIcon, ChevronRight, Edit3,
  Columns, Settings, Filter, GripVertical, Paperclip, Download, Image,
  FileText, File, List, Search,
} from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';
const f = (url, opts = {}) => fetch(`${API}${url}`, { credentials: 'include', ...opts });

// ─── Helpers ────────────────────────────────────────────────────────────────
const todayISO = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
};

const PRIO_BORDER = {
  urgent: 'border-l-red-500',
  high:   'border-l-amber-500',
  normal: 'border-l-transparent',
  low:    'border-l-gray-300',
};

const PRIO_LABEL = (t) => ({
  urgent: t('meineAufgaben.prio.urgent', 'Dringend'),
  high:   t('meineAufgaben.prio.high', 'Hoch'),
  normal: t('meineAufgaben.prio.normal', 'Normal'),
  low:    t('meineAufgaben.prio.low', 'Niedrig'),
});

const KAT_LABEL = (t) => ({
  deep_work: t('meineAufgaben.kat.deepWork', 'Deep Work'),
  meeting:   t('meineAufgaben.kat.meeting', 'Meeting'),
  admin:     t('meineAufgaben.kat.admin', 'Admin'),
  sales:     t('meineAufgaben.kat.sales', 'Sales'),
  hr:        t('meineAufgaben.kat.hr', 'HR'),
  ops:       t('meineAufgaben.kat.ops', 'Ops'),
  followup:  t('meineAufgaben.kat.followup', 'Follow-up'),
});

const SOURCE_BADGE = {
  manual:    { label: 'Manuell',  color: '#6B7280', bg: '#F3F4F6', icon: Plus },
  chatter:   { label: 'Chatter',  color: '#338edc', bg: '#E0F2FE', icon: MessageSquare },
  system:    { label: 'System',   color: '#7C3AED', bg: '#EDE9FE', icon: Bot },
  calendly:  { label: 'Calendly', color: '#0891B2', bg: '#CFFAFE', icon: Calendar },
  calendar:  { label: 'Kalender', color: '#0891B2', bg: '#CFFAFE', icon: Calendar },
  onboarding:{ label: 'Onboarding', color: '#059669', bg: '#D1FAE5', icon: CheckCircle2 },
  case:      { label: 'Case',     color: '#DC2626', bg: '#FEE2E2', icon: AlertCircle },
};

const ENTITY_PATH = {
  kunde:        (id) => `/kunden/${id}`,
  case:         (id) => `/cases/${id}`,
  bewerber:     (id) => `/recruiting/bewerber/${id}`,
  mitarbeiter:  (id) => `/mitarbeiter/${id}`,
  lead:         (id) => `/sales/leads/${id}`,
  forecast:     ()   => `/forecast`,
};

const STATUS_LABELS = (t) => ({
  offen:          t('meineAufgaben.status.open', 'Offen'),
  in_bearbeitung: t('meineAufgaben.status.inProgress', 'In Bearbeitung'),
  erledigt:       t('meineAufgaben.status.done', 'Erledigt'),
  verschoben:     t('meineAufgaben.status.postponed', 'Verschoben'),
});

function formatDueLabel(date, t) {
  if (!date) return null;
  const d = new Date(typeof date === 'string' && date.length === 10 ? date + 'T00:00:00' : date);
  if (isNaN(d.getTime())) return null;
  const today = new Date(); today.setHours(0,0,0,0);
  const diff = Math.round((d.getTime() - today.getTime()) / 86400000);
  if (diff < 0)  return t('meineAufgaben.overdueDays', '{{n}} Tage ueberfaellig').replace('{{n}}', String(Math.abs(diff)));
  if (diff === 0) return t('meineAufgaben.today', 'Heute');
  if (diff === 1) return t('meineAufgaben.tomorrow', 'Morgen');
  if (diff <= 7) return t('meineAufgaben.inDays', 'In {{n}} Tagen').replace('{{n}}', String(diff));
  return d.toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit' });
}

function initials(name) {
  if (!name) return '?';
  const p = name.trim().split(/\s+/);
  return ((p[0]?.[0] || '') + (p[1]?.[0] || '')).toUpperCase() || '?';
}

function Avatar({ name, size = 28 }) {
  return (
    <div
      style={{
        width: size, height: size, borderRadius: '50%',
        background: 'linear-gradient(135deg, #338edc, #6366F1)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, color: '#fff', fontSize: Math.max(10, Math.round(size * 0.38)), fontWeight: 700,
      }}
    >
      {initials(name)}
    </div>
  );
}

// ─── Task Card ──────────────────────────────────────────────────────────────
function TaskCard({ task, onToggleDone, onDelete, onClick, onDragStart, t, draggable = false, showAssignee = false, categories = [] }) {
  const navigate = useNavigate();
  const prioBorder = PRIO_BORDER[task.priority] || PRIO_BORDER.normal;
  const src = SOURCE_BADGE[task.source] || SOURCE_BADGE.manual;
  const SrcIcon = src.icon;
  const taskCat = task.category_id ? categories.find(c => c.id === task.category_id) : null;
  const due = formatDueLabel(task.due_date, t);
  const isOverdue = task.due_date && new Date(task.due_date) < new Date(todayISO());
  const isDone = task.status === 'erledigt';

  const handleEntityClick = (e) => {
    e.stopPropagation();
    if (task.entity_type && task.entity_id && ENTITY_PATH[task.entity_type]) {
      navigate(ENTITY_PATH[task.entity_type](task.entity_id));
    }
  };

  return (
    <div
      draggable={draggable}
      onDragStart={onDragStart}
      onClick={onClick}
      className={`group bg-[var(--bg-card,#fff)] border border-l-4 ${prioBorder} rounded-lg px-3 py-2.5 cursor-pointer hover:border-[#338edc] transition-colors ${isDone ? 'opacity-60' : ''}`}
      style={{ borderRightColor: 'var(--border-card, #E5E7EB)', borderTopColor: 'var(--border-card, #E5E7EB)', borderBottomColor: 'var(--border-card, #E5E7EB)' }}
    >
      <div className="flex items-start gap-2">
        <button
          onClick={(e) => { e.stopPropagation(); onToggleDone(task); }}
          className={`mt-0.5 w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors ${
            isDone
              ? 'bg-[#22C55E] border-[#22C55E]'
              : 'border-[var(--border-default,#D1D5DB)] hover:border-[#338edc]'
          }`}
          title={isDone ? t('meineAufgaben.markOpen', 'Wieder oeffnen') : t('meineAufgaben.markDone', 'Erledigt')}
        >
          {isDone && <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <p className={`text-[13px] font-medium leading-tight ${isDone ? 'line-through' : ''}`} style={{ color: 'var(--text-primary, #1A1A2E)' }}>
              {task.title}
            </p>
            {task.estimated_minutes ? (
              <span className="text-[11px] flex items-center gap-1 flex-shrink-0" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
                <Clock className="w-3 h-3" /> {task.estimated_minutes}min
              </span>
            ) : null}
          </div>
          {task.description && (
            <p className="text-[11px] mt-0.5 line-clamp-2" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
              {task.description}
            </p>
          )}
          <div className="flex items-center flex-wrap gap-2 mt-1.5">
            <span
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide"
              style={{ background: src.bg, color: src.color }}
            >
              <SrcIcon className="w-3 h-3" /> {src.label}
            </span>
            {task.entity_type && task.entity_id && (
              <button
                onClick={handleEntityClick}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium hover:underline"
                style={{ color: 'var(--text-secondary, #4B5563)', background: 'var(--bg-inset, #F3F4F6)' }}
              >
                <LinkIcon className="w-3 h-3" />
                {task.entity_label || `${task.entity_type} #${task.entity_id}`}
              </button>
            )}
            {due && (
              <span className={`text-[11px] font-medium ${isOverdue && !isDone ? 'text-red-500' : ''}`} style={{ color: isOverdue && !isDone ? undefined : 'var(--text-tertiary, #6B7280)' }}>
                {due}
              </span>
            )}
            {showAssignee && task.assigned_name && (
              <span className="inline-flex items-center gap-1 text-[10px]" style={{ color: 'var(--text-secondary, #4B5563)' }}>
                <Avatar name={task.assigned_name} size={16} />
                {task.assigned_name}
              </span>
            )}
            {taskCat && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium"
                style={{ background: 'var(--bg-inset, #F3F4F6)', color: 'var(--text-secondary, #4B5563)' }}>
                <span className="w-2 h-2 rounded-sm flex-shrink-0" style={{ background: taskCat.color || '#338EDC' }} />
                {taskCat.name}
              </span>
            )}
            {!showAssignee && task.creator_name && task.created_by !== task.assigned_to && (
              <span className="text-[10px]" style={{ color: 'var(--text-muted, #9CA3AF)' }}>
                {t('meineAufgaben.by', 'von')} {task.creator_name}
              </span>
            )}
          </div>
          {/* Last comment preview for "Von mir erstellt" */}
          {showAssignee && task.last_comment && (
            <p className="mt-1 text-[11px] truncate" style={{ color: 'var(--text-muted, #9CA3AF)' }}>
              <MessageSquare className="w-3 h-3 inline -mt-0.5 mr-1" />
              {task.last_comment.length > 80 ? task.last_comment.slice(0, 80) + '...' : task.last_comment}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1">
          <ChevronRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-60" style={{ color: 'var(--text-muted, #9CA3AF)' }} />
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(task); }}
            className="opacity-0 group-hover:opacity-100 text-[var(--text-muted,#9CA3AF)] hover:text-red-500 transition-opacity"
            title={t('meineAufgaben.delete', 'Loeschen')}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Create Modal (only for new tasks) ──────────────────────────────────────
function CreateModal({ open, onClose, onSaved, categories: propCategories, t }) {
  const [form, setForm] = useState({
    title: '', description: '', due_date: '', estimated_minutes: '',
    priority: 'normal', kategorie: '', entity_type: '', entity_id: '', entity_label: '', assigned_to: '', category_id: '',
  });
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    const defCat = (propCategories || []).find(c => c.is_default) || (propCategories || [])[0];
    setForm({ title: '', description: '', due_date: '', estimated_minutes: '', priority: 'normal', kategorie: '', entity_type: '', entity_id: '', entity_label: '', assigned_to: '', category_id: defCat ? String(defCat.id) : '' });
    f('/api/chatter/users/search?q=a').then(r => r.ok ? r.json() : { users: [] }).then(d => setUsers(d.users || [])).catch(() => {});
  }, [open, propCategories]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { setError(t('meineAufgaben.titleRequired', 'Titel erforderlich')); return; }
    setSaving(true); setError(null);
    const payload = {
      title: form.title.trim(), description: form.description || null, due_date: form.due_date || null,
      estimated_minutes: form.estimated_minutes ? parseInt(form.estimated_minutes, 10) : null,
      priority: form.priority, kategorie: form.kategorie || null, entity_type: form.entity_type || null,
      entity_id: form.entity_id ? parseInt(form.entity_id, 10) : null, entity_label: form.entity_label || null,
      assigned_to: form.assigned_to ? parseInt(form.assigned_to, 10) : null,
      category_id: form.category_id ? parseInt(form.category_id, 10) : null,
    };
    try {
      const r = await f('/api/user-tasks', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.error || `${r.status}`); }
      onSaved(); onClose();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const prioLabels = PRIO_LABEL(t);
  const katLabels = KAT_LABEL(t);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-[var(--bg-card,#fff)] rounded-xl border w-full max-w-lg max-h-[90vh] overflow-y-auto" style={{ borderColor: 'var(--border-card, #E5E7EB)' }}>
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'var(--border-card, #E5E7EB)' }}>
          <h2 className="text-base font-bold" style={{ color: 'var(--text-primary, #1A1A2E)' }}>{t('meineAufgaben.newTask', 'Neue Aufgabe')}</h2>
          <button onClick={onClose} className="text-[var(--text-tertiary,#6B7280)] hover:text-[var(--text-primary)]"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3">
          <div>
            <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary, #4B5563)' }}>{t('meineAufgaben.title', 'Titel')} *</label>
            <input type="text" value={form.title} onChange={e => setForm(s => ({ ...s, title: e.target.value }))} autoFocus
              className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border-default, #D1D5DB)', background: 'var(--bg-input, #fff)', color: 'var(--text-primary, #1A1A2E)' }} />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary, #4B5563)' }}>{t('meineAufgaben.description', 'Beschreibung')}</label>
            <textarea value={form.description} onChange={e => setForm(s => ({ ...s, description: e.target.value }))} rows={2}
              className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border-default, #D1D5DB)', background: 'var(--bg-input, #fff)', color: 'var(--text-primary, #1A1A2E)' }} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary, #4B5563)' }}>{t('meineAufgaben.dueDate', 'Faellig am')}</label>
              <input type="date" value={form.due_date} onChange={e => setForm(s => ({ ...s, due_date: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border-default, #D1D5DB)', background: 'var(--bg-input, #fff)', color: 'var(--text-primary, #1A1A2E)' }} />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary, #4B5563)' }}>{t('meineAufgaben.duration', 'Dauer (min)')}</label>
              <input type="number" min="0" value={form.estimated_minutes} onChange={e => setForm(s => ({ ...s, estimated_minutes: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border-default, #D1D5DB)', background: 'var(--bg-input, #fff)', color: 'var(--text-primary, #1A1A2E)' }} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary, #4B5563)' }}>{t('meineAufgaben.priority', 'Prioritaet')}</label>
              <select value={form.priority} onChange={e => setForm(s => ({ ...s, priority: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border-default, #D1D5DB)', background: 'var(--bg-input, #fff)', color: 'var(--text-primary, #1A1A2E)' }}>
                {Object.entries(prioLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary, #4B5563)' }}>{t('meineAufgaben.category', 'Kategorie')}</label>
              <select value={form.kategorie} onChange={e => setForm(s => ({ ...s, kategorie: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border-default, #D1D5DB)', background: 'var(--bg-input, #fff)', color: 'var(--text-primary, #1A1A2E)' }}>
                <option value="">—</option>
                {Object.entries(katLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          </div>
          {(propCategories || []).length > 0 && (
            <div>
              <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary, #4B5563)' }}>{t('meineAufgaben.taskCategory', 'Board-Kategorie')}</label>
              <select value={form.category_id} onChange={e => setForm(s => ({ ...s, category_id: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border-default, #D1D5DB)', background: 'var(--bg-input, #fff)', color: 'var(--text-primary, #1A1A2E)' }}>
                <option value="">—</option>
                {(propCategories || []).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary, #4B5563)' }}>{t('meineAufgaben.linkType', 'Verknuepfen mit')}</label>
              <select value={form.entity_type} onChange={e => setForm(s => ({ ...s, entity_type: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border-default, #D1D5DB)', background: 'var(--bg-input, #fff)', color: 'var(--text-primary, #1A1A2E)' }}>
                <option value="">—</option>
                <option value="kunde">{t('meineAufgaben.entity.kunde', 'Kunde')}</option>
                <option value="case">{t('meineAufgaben.entity.case', 'Case')}</option>
                <option value="bewerber">{t('meineAufgaben.entity.bewerber', 'Bewerber')}</option>
                <option value="mitarbeiter">{t('meineAufgaben.entity.mitarbeiter', 'Mitarbeiter')}</option>
                <option value="lead">{t('meineAufgaben.entity.lead', 'Lead')}</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary, #4B5563)' }}>{t('meineAufgaben.linkId', 'Objekt-ID')}</label>
              <input type="number" value={form.entity_id} onChange={e => setForm(s => ({ ...s, entity_id: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border-default, #D1D5DB)', background: 'var(--bg-input, #fff)', color: 'var(--text-primary, #1A1A2E)' }} />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary, #4B5563)' }}>{t('meineAufgaben.assignTo', 'Zuweisen an')}</label>
            <select value={form.assigned_to} onChange={e => setForm(s => ({ ...s, assigned_to: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border-default, #D1D5DB)', background: 'var(--bg-input, #fff)', color: 'var(--text-primary, #1A1A2E)' }}>
              <option value="">{t('meineAufgaben.assignSelf', 'Mir selbst')}</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
          {error && <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 text-red-700 text-xs"><AlertCircle className="w-4 h-4" /> {error}</div>}
          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm border" style={{ borderColor: 'var(--border-default, #D1D5DB)', color: 'var(--text-secondary, #4B5563)' }}>
              {t('meineAufgaben.cancel', 'Abbrechen')}
            </button>
            <button type="submit" disabled={saving} className="px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50" style={{ background: '#338edc' }}>
              {saving ? '...' : t('meineAufgaben.create', 'Erstellen')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Filter Bar ──────────────────────────────────────────────────────────────
function FilterBar({ filters, setFilters, stages, categories, t }) {
  const [open, setOpen] = useState(false);

  const toggle = (key, val) => {
    setFilters(prev => {
      const arr = prev[key] || [];
      return { ...prev, [key]: arr.includes(val) ? arr.filter(v => v !== val) : [...arr, val] };
    });
  };

  const removeChip = (key, val) => {
    setFilters(prev => ({ ...prev, [key]: (prev[key] || []).filter(v => v !== val) }));
  };

  const activeCount = Object.values(filters).reduce((s, a) => s + a.length, 0);

  const sections = [
    { key: 'priority', label: t('meineAufgaben.filter.priority', 'Prioritaet'), options: [
      { value: 'urgent', label: t('meineAufgaben.prio.urgent', 'Dringend'), color: '#EF4444' },
      { value: 'high', label: t('meineAufgaben.prio.high', 'Hoch'), color: '#F59E0B' },
      { value: 'normal', label: t('meineAufgaben.prio.normal', 'Normal'), color: '#6B7280' },
      { value: 'low', label: t('meineAufgaben.prio.low', 'Niedrig'), color: '#94A3B8' },
    ]},
    { key: 'source', label: t('meineAufgaben.filter.source', 'Quelle'), options: [
      { value: 'manual', label: 'Manuell' }, { value: 'chatter', label: 'Chatter' },
      { value: 'system', label: 'System' }, { value: 'telegram', label: 'Telegram' },
      { value: 'case', label: 'Case' }, { value: 'calendar', label: 'Kalender' },
    ]},
    { key: 'entity_type', label: t('meineAufgaben.filter.entityType', 'Verknuepfung'), options: [
      { value: 'kunde', label: t('meineAufgaben.entity.kunde', 'Kunde') },
      { value: 'case', label: t('meineAufgaben.entity.case', 'Case') },
      { value: 'bewerber', label: t('meineAufgaben.entity.bewerber', 'Bewerber') },
      { value: 'mitarbeiter', label: t('meineAufgaben.entity.mitarbeiter', 'Mitarbeiter') },
    ]},
  ];

  if (categories.length > 0) {
    sections.unshift({
      key: 'category_id', label: t('meineAufgaben.filter.category', 'Kategorie'),
      options: categories.map(c => ({ value: String(c.id), label: c.name, color: c.color })),
    });
  }
  if (stages.length > 0) {
    sections.splice(categories.length > 0 ? 1 : 0, 0, {
      key: 'stage_id', label: t('meineAufgaben.filter.stage', 'Stage'),
      options: stages.map(s => ({ value: String(s.id), label: s.name, color: s.color })),
    });
  }

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <div className="relative">
        <button onClick={() => setOpen(o => !o)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${activeCount > 0 ? 'border-[#338edc] text-[#338edc]' : ''}`}
          style={activeCount > 0 ? {} : { borderColor: 'var(--border-default,#D1D5DB)', color: 'var(--text-secondary,#4B5563)' }}>
          <Filter className="w-3.5 h-3.5" />
          {t('meineAufgaben.filter.label', 'Filter')}
          {activeCount > 0 && <span className="ml-1 bg-[#338edc] text-white rounded-full w-4 h-4 text-[10px] flex items-center justify-center">{activeCount}</span>}
        </button>
        {open && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
            <div className="absolute z-50 top-full mt-1 left-0 w-72 rounded-xl border bg-[var(--bg-card,#fff)] shadow-sm p-3 space-y-3" style={{ borderColor: 'var(--border-card,#E5E7EB)' }}>
              {sections.map(sec => (
                <div key={sec.key}>
                  <p className="text-[10px] font-bold uppercase tracking-wider mb-1.5" style={{ color: 'var(--text-muted,#9CA3AF)' }}>{sec.label}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {sec.options.map(opt => {
                      const active = (filters[sec.key] || []).includes(opt.value);
                      return (
                        <button key={opt.value} onClick={() => toggle(sec.key, opt.value)}
                          className={`px-2 py-1 rounded-lg text-[11px] font-medium border transition-colors ${active ? 'border-[#338edc] bg-blue-50 text-[#338edc]' : ''}`}
                          style={active ? {} : { borderColor: 'var(--border-default,#D1D5DB)', color: 'var(--text-secondary,#4B5563)' }}>
                          {opt.color && <span className="inline-block w-2 h-2 rounded-full mr-1" style={{ background: opt.color }} />}
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
              {activeCount > 0 && (
                <button onClick={() => setFilters({ priority: [], source: [], entity_type: [], stage_id: [], category_id: [] })}
                  className="text-[11px] text-red-500 hover:underline">{t('meineAufgaben.filter.clearAll', 'Alle Filter entfernen')}</button>
              )}
            </div>
          </>
        )}
      </div>
      {/* Active filter chips */}
      {sections.flatMap(sec => (filters[sec.key] || []).map(val => {
        const opt = sec.options.find(o => o.value === val);
        return (
          <span key={`${sec.key}-${val}`}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-50 text-[#338edc] border border-[#338edc]/20">
            {opt?.color && <span className="w-1.5 h-1.5 rounded-full" style={{ background: opt.color }} />}
            {opt?.label || val}
            <button onClick={() => removeChip(sec.key, val)} className="hover:text-red-500"><X className="w-3 h-3" /></button>
          </span>
        );
      }))}
    </div>
  );
}

// ─── Kanban Card (compact) ──────────────────────────────────────────────────
function KanbanCard({ task, onClick, onToggleDone, t, categories = [] }) {
  const prioBorder = PRIO_BORDER[task.priority] || PRIO_BORDER.normal;
  const src = SOURCE_BADGE[task.source] || SOURCE_BADGE.manual;
  const SrcIcon = src.icon;
  const taskCat = task.category_id ? categories.find(c => c.id === task.category_id) : null;
  const due = formatDueLabel(task.due_date, t);
  const isOverdue = task.due_date && new Date(task.due_date) < new Date(todayISO());
  const isDone = task.status === 'erledigt';

  return (
    <div
      draggable
      onDragStart={e => { e.dataTransfer.setData('text/plain', String(task.id)); e.dataTransfer.effectAllowed = 'move'; }}
      onClick={onClick}
      className={`bg-[var(--bg-card,#fff)] border border-l-[3px] ${prioBorder} rounded-lg px-2.5 py-2 cursor-grab active:cursor-grabbing hover:border-[#338edc] transition-all ${isDone ? 'opacity-50' : ''}`}
      style={{ borderRightColor: 'var(--border-card,#E5E7EB)', borderTopColor: 'var(--border-card,#E5E7EB)', borderBottomColor: 'var(--border-card,#E5E7EB)' }}
    >
      <div className="flex items-start gap-1.5">
        <button onClick={e => { e.stopPropagation(); onToggleDone(task); }}
          className={`mt-0.5 w-3.5 h-3.5 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${isDone ? 'bg-[#22C55E] border-[#22C55E]' : 'border-[var(--border-default,#D1D5DB)] hover:border-[#338edc]'}`}>
          {isDone && <svg className="w-2 h-2 text-white" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>}
        </button>
        <div className="flex-1 min-w-0">
          <p className={`text-[12px] font-medium leading-tight ${isDone ? 'line-through' : ''}`} style={{ color: 'var(--text-primary,#1A1A2E)' }}>
            {task.title}
          </p>
          {task.description && (
            <p className="text-[10px] mt-0.5 truncate" style={{ color: 'var(--text-muted,#9CA3AF)' }}>
              {task.description}
            </p>
          )}
          <div className="flex items-center gap-1.5 mt-1">
            <span className="inline-flex items-center gap-0.5 text-[9px] font-semibold uppercase" style={{ color: src.color }}>
              <SrcIcon className="w-2.5 h-2.5" />
            </span>
            {due && <span className={`text-[10px] ${isOverdue && !isDone ? 'text-red-500' : ''}`} style={{ color: isOverdue && !isDone ? undefined : 'var(--text-muted,#9CA3AF)' }}>{due}</span>}
            {task.entity_type && <LinkIcon className="w-2.5 h-2.5" style={{ color: 'var(--text-muted,#9CA3AF)' }} />}
            {taskCat && (
              <span className="inline-flex items-center gap-0.5 text-[9px] font-medium" style={{ color: 'var(--text-muted,#9CA3AF)' }}>
                <span className="w-1.5 h-1.5 rounded-sm" style={{ background: taskCat.color || '#338EDC' }} />
                {taskCat.name}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Kanban Column ──────────────────────────────────────────────────────────
function KanbanColumn({ stage, tasks, onDrop, onCardClick, onToggleDone, t, categories }) {
  const [dragOver, setDragOver] = useState(false);

  return (
    <div
      onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={e => { e.preventDefault(); setDragOver(false); const tid = parseInt(e.dataTransfer.getData('text/plain'), 10); if (tid) onDrop(tid, stage.id); }}
      className={`flex flex-col min-w-[260px] max-w-[320px] w-full rounded-xl border transition-colors ${dragOver ? 'border-[#338edc] bg-blue-50/30' : ''}`}
      style={{ borderColor: dragOver ? '#338edc' : 'var(--border-card,#E5E7EB)', background: dragOver ? undefined : 'var(--bg-inset,#F8F9FC)' }}
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2.5 border-b" style={{ borderColor: 'var(--border-card,#E5E7EB)' }}>
        <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ background: stage.color || '#94A3B8' }} />
        <h3 className="text-xs font-bold uppercase tracking-wider flex-1" style={{ color: 'var(--text-secondary,#4B5563)' }}>
          {stage.name}
        </h3>
        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full" style={{ background: 'var(--bg-card,#fff)', color: 'var(--text-muted,#9CA3AF)' }}>
          {tasks.length}
        </span>
      </div>
      {/* Cards */}
      <div className="flex-1 p-2 space-y-1.5 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 340px)' }}>
        {tasks.length === 0 && (
          <p className="text-[11px] text-center py-6" style={{ color: 'var(--text-muted,#9CA3AF)' }}>
            {t('meineAufgaben.empty.stage', 'Keine Aufgaben')}
          </p>
        )}
        {tasks.map(task => (
          <KanbanCard key={task.id} task={task} onClick={() => onCardClick(task)} onToggleDone={onToggleDone} t={t} categories={categories} />
        ))}
      </div>
    </div>
  );
}

// ─── Category + Stage Editor Modal (2-Level) ────────────────────────────────
function CategoryStageEditor({ open, onClose, categories, onRefresh, t }) {
  const [catList, setCatList] = useState([]);
  const [selectedCatId, setSelectedCatId] = useState(null);
  const [stageList, setStageList] = useState([]);
  const [newCatName, setNewCatName] = useState('');
  const [newCatColor, setNewCatColor] = useState('#338EDC');
  const [newStageName, setNewStageName] = useState('');
  const [newStageColor, setNewStageColor] = useState('#94A3B8');
  const [saving, setSaving] = useState(false);
  const [stagesLoading, setStagesLoading] = useState(false);
  const [confirmDeleteCat, setConfirmDeleteCat] = useState(null);
  const [confirmDeleteStage, setConfirmDeleteStage] = useState(null);
  const [editorError, setEditorError] = useState(null);
  const [stageReloadKey, setStageReloadKey] = useState(0);

  const COLORS = ['#94A3B8', '#338EDC', '#F59E0B', '#8B5CF6', '#10B981', '#EF4444', '#EC4899', '#6366F1', '#14B8A6', '#F97316'];

  useEffect(() => {
    if (open) {
      setCatList(categories.map(c => ({ ...c })));
      if (categories.length > 0 && !selectedCatId) setSelectedCatId(categories[0].id);
    }
  }, [open, categories]);

  // Load stages for selected category
  useEffect(() => {
    if (!selectedCatId || !open) return;
    setStagesLoading(true);
    setEditorError(null);
    f(`/api/user-tasks/stages?category_id=${selectedCatId}`).then(r => r.ok ? r.json() : { stages: [] })
      .then(d => setStageList((d.stages || []).map(s => ({ ...s }))))
      .catch(() => {}).finally(() => setStagesLoading(false));
  }, [selectedCatId, open, stageReloadKey]);

  if (!open) return null;

  const selectedCat = catList.find(c => c.id === selectedCatId);

  // ── Category Actions ──
  const handleAddCategory = async () => {
    if (!newCatName.trim()) return;
    setSaving(true);
    try {
      const r = await f('/api/user-tasks/categories', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newCatName.trim(), color: newCatColor }),
      });
      if (r.ok) {
        const d = await r.json();
        setNewCatName(''); setNewCatColor('#338EDC');
        setSelectedCatId(d.category.id);
        onRefresh();
      }
    } catch { /* */ }
    setSaving(false);
  };

  const handleSaveCategory = async (cat) => {
    setSaving(true);
    try {
      await f(`/api/user-tasks/categories/${cat.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: cat.name, color: cat.color }),
      });
      onRefresh();
    } catch { /* */ }
    setSaving(false);
  };

  const handleDeleteCategory = (cat) => setConfirmDeleteCat(cat);
  const executeDeleteCategory = async () => {
    const cat = confirmDeleteCat;
    setConfirmDeleteCat(null);
    if (!cat) return;
    try {
      const r = await f(`/api/user-tasks/categories/${cat.id}`, { method: 'DELETE' });
      if (!r.ok) { const j = await r.json().catch(() => ({})); setEditorError(j.error || 'Fehler'); return; }
      if (selectedCatId === cat.id) {
        const remaining = catList.filter(c => c.id !== cat.id);
        setSelectedCatId(remaining[0]?.id || null);
      }
      onRefresh();
    } catch { /* */ }
  };

  // ── Stage Actions ──
  const handleSaveStage = async (stage) => {
    setSaving(true);
    try {
      await f(`/api/user-tasks/stages/${stage.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: stage.name, color: stage.color, is_done_stage: stage.is_done_stage }),
      });
      onRefresh();
    } catch { /* */ }
    setSaving(false);
  };

  const handleAddStage = async () => {
    if (!newStageName.trim() || !selectedCatId) return;
    setSaving(true);
    try {
      await f('/api/user-tasks/stages', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newStageName.trim(), color: newStageColor, category_id: selectedCatId }),
      });
      setNewStageName(''); setNewStageColor('#94A3B8');
      onRefresh();
    } catch { /* */ }
    setSaving(false);
  };

  const handleDeleteStage = (stage) => setConfirmDeleteStage(stage);
  const executeDeleteStage = async () => {
    const stage = confirmDeleteStage;
    setConfirmDeleteStage(null);
    if (!stage) return;
    try {
      const r = await f(`/api/user-tasks/stages/${stage.id}`, { method: 'DELETE' });
      if (!r.ok) { const j = await r.json().catch(() => ({})); setEditorError(j.error || 'Fehler'); return; }
      setStageReloadKey(k => k + 1);
      onRefresh();
    } catch { /* */ }
  };

  const handleReorderStages = async (fromIdx, toIdx) => {
    const reordered = [...stageList];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    setStageList(reordered);
    try {
      await f('/api/user-tasks/stages/reorder', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: reordered.map(s => s.id) }),
      });
      onRefresh();
    } catch { /* */ }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-[var(--bg-card,#fff)] rounded-xl border w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" style={{ borderColor: 'var(--border-card,#E5E7EB)' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'var(--border-card,#E5E7EB)' }}>
          <h2 className="text-base font-bold" style={{ color: 'var(--text-primary,#1A1A2E)' }}>{t('meineAufgaben.categories.editorTitle', 'Kategorien & Stages')}</h2>
          <button onClick={onClose} className="text-[var(--text-tertiary,#6B7280)] hover:text-[var(--text-primary)]"><X className="w-5 h-5" /></button>
        </div>
        {/* 2-Panel Layout */}
        <div className="flex flex-1 overflow-hidden">
          {/* LEFT: Categories */}
          <div className="w-[220px] border-r flex flex-col overflow-y-auto" style={{ borderColor: 'var(--border-card,#E5E7EB)', background: 'var(--bg-inset,#F8F9FC)' }}>
            <div className="p-3 space-y-1.5">
              <p className="text-[10px] font-bold uppercase tracking-wider px-1 mb-2" style={{ color: 'var(--text-muted,#9CA3AF)' }}>
                {t('meineAufgaben.categories.label', 'Kategorien')}
              </p>
              {catList.map(cat => (
                <button key={cat.id} onClick={() => setSelectedCatId(cat.id)}
                  className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-left text-xs font-medium transition-colors ${
                    selectedCatId === cat.id ? 'bg-white shadow-sm' : 'hover:bg-white/50'
                  }`}
                  style={{ color: selectedCatId === cat.id ? 'var(--text-primary,#1A1A2E)' : 'var(--text-secondary,#4B5563)' }}>
                  <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ background: cat.color || '#338EDC' }} />
                  <span className="truncate flex-1">{cat.name}</span>
                  {cat.is_default && <span className="text-[9px] px-1 py-0.5 rounded bg-blue-50 text-[#338edc]">Default</span>}
                </button>
              ))}
            </div>
            {/* Add Category */}
            <div className="p-3 mt-auto border-t" style={{ borderColor: 'var(--border-card,#E5E7EB)' }}>
              <div className="flex gap-1 mb-2">
                {COLORS.slice(0, 5).map(c => (
                  <button key={c} onClick={() => setNewCatColor(c)}
                    className={`w-4 h-4 rounded-full border-2 ${newCatColor === c ? 'border-[#338edc] scale-110' : 'border-transparent'}`}
                    style={{ background: c }} />
                ))}
              </div>
              <div className="flex items-center gap-1.5">
                <input type="text" value={newCatName} onChange={e => setNewCatName(e.target.value)}
                  placeholder={t('meineAufgaben.categories.newPlaceholder', 'Neue Kategorie...')}
                  className="flex-1 px-2 py-1.5 rounded-lg border text-xs" style={{ borderColor: 'var(--border-default,#D1D5DB)', background: 'var(--bg-input,#fff)', color: 'var(--text-primary,#1A1A2E)' }}
                  onKeyDown={e => { if (e.key === 'Enter') handleAddCategory(); }} />
                <button onClick={handleAddCategory} disabled={saving || !newCatName.trim()}
                  className="p-1.5 rounded-lg text-white disabled:opacity-50" style={{ background: '#338edc' }}>
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>

          {/* RIGHT: Stages of selected Category */}
          <div className="flex-1 flex flex-col overflow-y-auto">
            {selectedCat ? (
              <div className="p-4 space-y-3">
                {/* Category Edit Row */}
                <div className="flex items-center gap-2 p-2.5 rounded-lg border" style={{ borderColor: 'var(--border-default,#D1D5DB)' }}>
                  <input type="color" value={selectedCat.color || '#338EDC'}
                    onChange={e => { const c = e.target.value; setCatList(l => l.map(cat => cat.id === selectedCatId ? { ...cat, color: c } : cat)); }}
                    className="w-6 h-6 rounded cursor-pointer border-0 p-0" />
                  <input type="text" value={selectedCat.name}
                    onChange={e => { const n = e.target.value; setCatList(l => l.map(cat => cat.id === selectedCatId ? { ...cat, name: n } : cat)); }}
                    className="flex-1 px-2 py-1 rounded text-sm font-semibold border" style={{ borderColor: 'var(--border-default,#D1D5DB)', background: 'var(--bg-input,#fff)', color: 'var(--text-primary,#1A1A2E)' }} />
                  <button onClick={() => handleSaveCategory(selectedCat)} disabled={saving}
                    className="text-[10px] font-semibold text-[#338edc] hover:underline">{t('meineAufgaben.save', 'Speichern')}</button>
                  <button onClick={() => handleDeleteCategory(selectedCat)}
                    className="text-[var(--text-muted,#9CA3AF)] hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>

                {/* Stages List */}
                <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: 'var(--text-muted,#9CA3AF)' }}>
                  {t('meineAufgaben.stages.label', 'Stages')}
                </p>
                {stagesLoading ? (
                  <div className="py-4 text-center"><div className="inline-block w-5 h-5 border-2 border-[#338edc] border-t-transparent rounded-full animate-spin" /></div>
                ) : (
                  <div className="space-y-2">
                    {stageList.map((stage, idx) => (
                      <div key={stage.id} className="flex items-center gap-2 p-2 rounded-lg border" style={{ borderColor: 'var(--border-default,#D1D5DB)' }}>
                        <GripVertical className="w-4 h-4 flex-shrink-0 cursor-grab" style={{ color: 'var(--text-muted,#9CA3AF)' }}
                          draggable onDragStart={e => { e.dataTransfer.setData('stage-idx', String(idx)); e.dataTransfer.effectAllowed = 'move'; }}
                          onDragOver={e => e.preventDefault()}
                          onDrop={e => { e.preventDefault(); const from = parseInt(e.dataTransfer.getData('stage-idx'), 10); if (!isNaN(from) && from !== idx) handleReorderStages(from, idx); }} />
                        <input type="color" value={stage.color || '#94A3B8'}
                          onChange={e => { const c = e.target.value; setStageList(l => l.map((s, i) => i === idx ? { ...s, color: c } : s)); }}
                          className="w-6 h-6 rounded cursor-pointer border-0 p-0" />
                        <input type="text" value={stage.name}
                          onChange={e => { const n = e.target.value; setStageList(l => l.map((s, i) => i === idx ? { ...s, name: n } : s)); }}
                          className="flex-1 px-2 py-1 rounded text-sm border" style={{ borderColor: 'var(--border-default,#D1D5DB)', background: 'var(--bg-input,#fff)', color: 'var(--text-primary,#1A1A2E)' }} />
                        <label className="flex items-center gap-1 text-[10px] whitespace-nowrap" style={{ color: 'var(--text-tertiary,#6B7280)' }}>
                          <input type="checkbox" checked={!!stage.is_done_stage}
                            onChange={e => { const v = e.target.checked; setStageList(l => l.map((s, i) => i === idx ? { ...s, is_done_stage: v } : s)); }}
                            className="w-3 h-3 rounded" />
                          {t('meineAufgaben.stages.doneStage', 'Abschluss')}
                        </label>
                        <button onClick={() => handleSaveStage(stageList[idx])} disabled={saving}
                          className="text-[10px] font-semibold text-[#338edc] hover:underline">{t('meineAufgaben.save', 'Speichern')}</button>
                        <button onClick={() => handleDeleteStage(stage)}
                          className="text-[var(--text-muted,#9CA3AF)] hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add Stage */}
                <div className="flex items-center gap-2 pt-2 border-t" style={{ borderColor: 'var(--border-card,#E5E7EB)' }}>
                  <div className="flex gap-1">
                    {COLORS.map(c => (
                      <button key={c} onClick={() => setNewStageColor(c)}
                        className={`w-4 h-4 rounded-full border-2 ${newStageColor === c ? 'border-[#338edc] scale-110' : 'border-transparent'}`}
                        style={{ background: c }} />
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input type="text" value={newStageName} onChange={e => setNewStageName(e.target.value)}
                    placeholder={t('meineAufgaben.stages.newPlaceholder', 'Neue Stage...')}
                    className="flex-1 px-3 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border-default,#D1D5DB)', background: 'var(--bg-input,#fff)', color: 'var(--text-primary,#1A1A2E)' }}
                    onKeyDown={e => { if (e.key === 'Enter') handleAddStage(); }} />
                  <button onClick={handleAddStage} disabled={saving || !newStageName.trim()}
                    className="px-3 py-2 rounded-lg text-xs font-semibold text-white disabled:opacity-50" style={{ background: '#338edc' }}>
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center p-8">
                <p className="text-sm" style={{ color: 'var(--text-muted,#9CA3AF)' }}>{t('meineAufgaben.categories.selectHint', 'Kategorie auswaehlen')}</p>
              </div>
            )}
          </div>
        </div>
        {/* Error banner */}
        {editorError && (
          <div className="px-5 py-2.5 border-t flex items-center gap-2 bg-red-50" style={{ borderColor: 'var(--border-card,#E5E7EB)' }}>
            <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
            <p className="text-xs text-red-600 flex-1">{editorError}</p>
            <button onClick={() => setEditorError(null)} className="text-red-400 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
          </div>
        )}
      </div>
      <ConfirmDialog
        open={!!confirmDeleteCat}
        onConfirm={executeDeleteCategory}
        onCancel={() => setConfirmDeleteCat(null)}
        title={t('meineAufgaben.categories.deleteTitle', 'Kategorie loeschen')}
        message={confirmDeleteCat ? t('meineAufgaben.categories.deleteConfirm', 'Kategorie "{{name}}" wirklich loeschen?').replace('{{name}}', confirmDeleteCat.name) : ''}
        confirmLabel={t('meineAufgaben.delete', 'Loeschen')}
        cancelLabel={t('meineAufgaben.cancel', 'Abbrechen')}
        variant="danger"
      />
      <ConfirmDialog
        open={!!confirmDeleteStage}
        onConfirm={executeDeleteStage}
        onCancel={() => setConfirmDeleteStage(null)}
        title={t('meineAufgaben.stages.deleteTitle', 'Stage loeschen')}
        message={confirmDeleteStage ? t('meineAufgaben.stages.deleteConfirm', 'Stage "{{name}}" wirklich loeschen?').replace('{{name}}', confirmDeleteStage.name) : ''}
        confirmLabel={t('meineAufgaben.delete', 'Loeschen')}
        cancelLabel={t('meineAufgaben.cancel', 'Abbrechen')}
        variant="danger"
      />
    </div>
  );
}

// ─── Hauptseite ─────────────────────────────────────────────────────────────
export default function MeineAufgaben() {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('inbox');
  const [stats, setStats] = useState({ offen: 0, heute_faellig: 0, ueberfaellig: 0, delegiert_offen: 0, erledigt_7t: 0, inbox_count: 0 });
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [detailTask, setDetailTask] = useState(null);
  const [quickAdd, setQuickAdd] = useState('');
  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [stages, setStages] = useState([]);
  const [kanbanData, setKanbanData] = useState({ columns: [], unassigned: [] });
  const [editorOpen, setEditorOpen] = useState(false);
  const [filters, setFilters] = useState({ priority: [], source: [], entity_type: [], stage_id: [], category_id: [] });
  const [confirmDeleteMain, setConfirmDeleteMain] = useState(null);
  const [toast, setToast] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  // "Alle Aufgaben" server-side filters
  const [delegation, setDelegation] = useState('mine');
  const [dueFilter, setDueFilter] = useState('');
  const [showDone, setShowDone] = useState(false);
  const [sortBy, setSortBy] = useState('default');
  const [searchQ, setSearchQ] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const searchTimeout = useRef(null);

  useEffect(() => { if (toast) { const t = setTimeout(() => setToast(null), 3500); return () => clearTimeout(t); } }, [toast]);

  // Debounced search for "Alle Aufgaben"
  useEffect(() => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => setSearchQ(searchInput), 300);
    return () => { if (searchTimeout.current) clearTimeout(searchTimeout.current); };
  }, [searchInput]);

  // Deep-link: ?task=ID opens task detail
  useEffect(() => {
    const taskId = searchParams.get('task');
    if (taskId && !detailTask) {
      f(`/api/user-tasks/${taskId}`).then(r => r.ok ? r.json() : null).then(d => {
        if (d) { setDetailTask(d); setSearchParams({}, { replace: true }); }
      }).catch(() => {});
    }
  }, [searchParams]); // eslint-disable-line

  const loadStats = useCallback(async () => {
    try {
      const r = await f('/api/user-tasks/stats');
      if (r.ok) setStats(await r.json());
    } catch { /* */ }
  }, []);

  const loadCategories = useCallback(async () => {
    try {
      const r = await f('/api/user-tasks/categories');
      if (r.ok) {
        const d = await r.json();
        const cats = d.categories || [];
        setCategories(cats);
        // Auto-select default category if none selected
        if (!selectedCategoryId && cats.length > 0) {
          const def = cats.find(c => c.is_default) || cats[0];
          setSelectedCategoryId(def.id);
        }
      }
    } catch { /* */ }
  }, [selectedCategoryId]);

  const loadStages = useCallback(async () => {
    try {
      const url = selectedCategoryId ? `/api/user-tasks/stages?category_id=${selectedCategoryId}` : '/api/user-tasks/stages';
      const r = await f(url);
      if (r.ok) { const d = await r.json(); setStages(d.stages || []); }
    } catch { /* */ }
  }, [selectedCategoryId]);

  const loadKanban = useCallback(async () => {
    try {
      const url = selectedCategoryId ? `/api/user-tasks/kanban?category_id=${selectedCategoryId}` : '/api/user-tasks/kanban';
      const r = await f(url);
      if (r.ok) { const d = await r.json(); setKanbanData({ columns: d.columns || [], unassigned: d.unassigned || [] }); }
    } catch { /* */ }
  }, [selectedCategoryId]);

  const loadTasks = useCallback(async (tab) => {
    setLoading(true);
    try {
      if (tab === 'kanban') { await loadKanban(); setLoading(false); return; }
      if (tab === 'all') {
        const params = new URLSearchParams();
        if (delegation === 'created') params.set('delegation', 'created');
        else if (delegation === 'watching') params.set('delegation', 'watching');
        if (dueFilter) params.set('due_filter', dueFilter);
        if (showDone) params.set('show_done', 'true');
        if (sortBy !== 'default') params.set('sort_by', sortBy);
        if (searchQ.trim()) params.set('q', searchQ.trim());
        const qs = params.toString();
        const r = await f(`/api/user-tasks${qs ? `?${qs}` : ''}`);
        if (r.ok) { const d = await r.json(); setTasks(d.tasks || []); }
        setLoading(false);
        return;
      }
      // inbox
      const r = await f('/api/user-tasks/inbox');
      if (r.ok) { const d = await r.json(); setTasks(d.tasks || []); }
    } catch { /* */ }
    setLoading(false);
  }, [delegation, dueFilter, showDone, sortBy, searchQ, loadKanban]);

  useEffect(() => { loadStats(); loadCategories(); }, [loadStats, loadCategories]);
  useEffect(() => { loadStages(); }, [loadStages]);
  useEffect(() => { loadTasks(activeTab); }, [activeTab, loadTasks]);
  // Reload kanban when category changes
  useEffect(() => { if (activeTab === 'kanban') loadKanban(); }, [selectedCategoryId, activeTab, loadKanban]);

  const refresh = useCallback(() => {
    loadStats();
    loadTasks(activeTab);
    loadCategories();
    if (activeTab === 'kanban') loadStages();
  }, [loadStats, loadTasks, loadStages, loadCategories, activeTab]);

  // ─── Actions ────────────────────────────────────────────────────────────
  const handleToggleDone = async (task) => {
    const newStatus = task.status === 'erledigt' ? 'offen' : 'erledigt';
    try {
      await f(`/api/user-tasks/${task.id}/status`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      refresh();
    } catch { /* */ }
  };

  const handleDelete = (task) => setConfirmDeleteMain(task);
  const executeDeleteMain = async () => {
    const task = confirmDeleteMain;
    setConfirmDeleteMain(null);
    if (!task) return;
    try {
      await f(`/api/user-tasks/${task.id}`, { method: 'DELETE' });
      refresh();
    } catch { /* */ }
  };

  const handleQuickAdd = async (e) => {
    e.preventDefault();
    if (!quickAdd.trim()) return;
    try {
      await f('/api/user-tasks', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: quickAdd.trim(), due_date: todayISO(), priority: 'normal', source: 'manual' }),
      });
      setQuickAdd('');
      refresh();
    } catch { /* */ }
  };

  const handleStageDrop = async (taskId, newStageId) => {
    try {
      await f(`/api/user-tasks/${taskId}/stage`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stage_id: newStageId }),
      });
      refresh();
    } catch { /* */ }
  };

  const handleKanbanToggleDone = async (task) => {
    const isDone = task.status === 'erledigt';
    // Use stages from kanban columns for the current category
    const catStages = kanbanData.columns.map(c => c);
    if (isDone) {
      const def = catStages.find(s => s.is_default) || catStages[0];
      if (def) await handleStageDrop(task.id, def.id);
    } else {
      const done = catStages.find(s => s.is_done_stage);
      if (done) await handleStageDrop(task.id, done.id);
      else await handleToggleDone(task);
    }
  };

  // ─── Render Helpers ──────────────────────────────────────────────────────
  const tabs = [
    { key: 'inbox',   Icon: Inbox,   label: t('meineAufgaben.tab.inbox', 'Inbox'),         badge: stats.inbox_count },
    { key: 'all',     Icon: List,    label: t('meineAufgaben.tab.all', 'Alle Aufgaben'),   badge: 0 },
    { key: 'kanban',  Icon: Columns, label: t('meineAufgaben.tab.kanban', 'Kanban'),       badge: 0 },
  ];

  // Client-side filter logic
  const hasActiveFilters = Object.values(filters).some(a => a.length > 0);
  const applyFilters = useCallback((list) => {
    if (!hasActiveFilters) return list;
    return list.filter(t => {
      if (filters.priority.length > 0 && !filters.priority.includes(t.priority)) return false;
      if (filters.source.length > 0 && !filters.source.includes(t.source)) return false;
      if (filters.entity_type.length > 0 && !(t.entity_type && filters.entity_type.includes(t.entity_type))) return false;
      if (filters.stage_id.length > 0 && !filters.stage_id.includes(String(t.stage_id))) return false;
      if (filters.category_id.length > 0 && !filters.category_id.includes(String(t.category_id))) return false;
      return true;
    });
  }, [filters, hasActiveFilters]);

  const filteredTasks = useMemo(() => applyFilters(tasks), [tasks, applyFilters]);

  const kpiCards = [
    { label: t('meineAufgaben.kpi.open', 'Offen'),              value: stats.offen, icon: Inbox, color: '#338edc' },
    { label: t('meineAufgaben.kpi.today', 'Heute faellig'),     value: stats.heute_faellig, icon: Sun, color: '#F59E0B' },
    { label: t('meineAufgaben.kpi.overdue', 'Ueberfaellig'),    value: stats.ueberfaellig, alert: stats.ueberfaellig > 0, icon: AlertCircle, color: '#EF4444' },
    { label: t('meineAufgaben.kpi.delegated', 'Delegiert offen'), value: stats.delegiert_offen, icon: Users, color: '#8B5CF6' },
    { label: t('meineAufgaben.kpi.doneWeek', 'Erledigt (7T)'),  value: stats.erledigt_7t, icon: CheckCircle2, color: '#10B981' },
  ];

  // ─── JSX ─────────────────────────────────────────────────────────────────
  return (
    <div className="p-6 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
            {t('meineAufgaben.title', 'Meine Aufgaben')}
          </h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
            {t('meineAufgaben.subtitle', 'Persoenliches Task-Management')}
          </p>
        </div>
        <button
          onClick={() => setCreateOpen(true)}
          className="px-4 py-2 rounded-xl text-sm font-semibold text-white flex items-center gap-2"
          style={{ background: '#338edc' }}
        >
          <Plus className="w-4 h-4" /> {t('meineAufgaben.newTask', 'Neue Aufgabe')}
        </button>
      </div>

      {/* KPI-Bar */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {kpiCards.map((kpi, i) => (
          <div key={i} className={`bg-[var(--bg-card,#fff)] rounded-xl border p-4 ${kpi.alert ? 'border-l-4 border-l-red-400' : ''}`} style={{ borderColor: kpi.alert ? undefined : 'var(--border-card, #E5E7EB)' }}>
            <div className="flex items-center gap-2 mb-1">
              <kpi.icon size={16} style={{ color: kpi.color }} />
              <p className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-tertiary, #6B7280)' }}>{kpi.label}</p>
            </div>
            <p className="text-2xl font-bold tabular-nums" style={{ color: 'var(--text-primary, #1A1A2E)' }}>{kpi.value}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border w-fit overflow-x-auto" style={{ borderColor: 'var(--border-default,#E5E7EB)' }}>
        {tabs.map(tab => {
          const TabIcon = tab.Icon;
          const active = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-[8px] text-xs font-medium transition-all whitespace-nowrap ${
                active ? 'bg-white shadow-sm font-semibold dark:bg-[var(--bg-card)]' : 'hover:text-[var(--text-primary)]'
              }`}
              style={{ color: active ? 'var(--text-primary, #1A1A2E)' : 'var(--text-secondary, #4B5563)' }}
            >
              <TabIcon className="w-3.5 h-3.5" />
              {tab.label}
              {tab.badge > 0 && (
                <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold text-white"
                  style={{ background: '#338edc' }}>
                  {tab.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Inbox: simple filter bar */}
      {activeTab === 'inbox' && (
        <FilterBar filters={filters} setFilters={setFilters} stages={stages} categories={categories} t={t} />
      )}

      {/* "Alle Aufgaben" Filters */}
      {activeTab === 'all' && (
        <div className="space-y-3">
          {/* Row 1: Delegation + Due filter chips */}
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-1 bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border" style={{ borderColor: 'var(--border-default,#E5E7EB)' }}>
              {[
                { key: 'mine', label: t('meineAufgaben.delegation.mine', 'Meine') },
                { key: 'created', label: t('meineAufgaben.delegation.created', 'Von mir erstellt') },
                { key: 'watching', label: t('meineAufgaben.delegation.watching', 'Beobachtet') },
              ].map(opt => (
                <button key={opt.key} onClick={() => setDelegation(opt.key)}
                  className={`px-3 py-1.5 rounded-[8px] text-xs font-medium transition-all whitespace-nowrap ${
                    delegation === opt.key ? 'bg-white shadow-sm font-semibold dark:bg-[var(--bg-card)]' : 'hover:text-[var(--text-primary)]'
                  }`}
                  style={{ color: delegation === opt.key ? 'var(--text-primary,#1A1A2E)' : 'var(--text-secondary,#4B5563)' }}>
                  {opt.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1 bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border" style={{ borderColor: 'var(--border-default,#E5E7EB)' }}>
              {[
                { key: '', label: t('meineAufgaben.due.all', 'Alle') },
                { key: 'overdue', label: t('meineAufgaben.due.overdue', 'Ueberfaellig') },
                { key: 'today', label: t('meineAufgaben.due.today', 'Heute') },
                { key: 'this_week', label: t('meineAufgaben.due.thisWeek', 'Diese Woche') },
                { key: 'later', label: t('meineAufgaben.due.later', 'Spaeter') },
                { key: 'no_date', label: t('meineAufgaben.due.noDate', 'Ohne Datum') },
              ].map(opt => (
                <button key={opt.key} onClick={() => setDueFilter(opt.key)}
                  className={`px-3 py-1.5 rounded-[8px] text-xs font-medium transition-all whitespace-nowrap ${
                    dueFilter === opt.key ? 'bg-white shadow-sm font-semibold dark:bg-[var(--bg-card)]' : 'hover:text-[var(--text-primary)]'
                  }`}
                  style={{ color: dueFilter === opt.key ? 'var(--text-primary,#1A1A2E)' : 'var(--text-secondary,#4B5563)' }}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          {/* Row 2: Filter dropdown + Sort + Show Done + Search */}
          <div className="flex items-center gap-3 flex-wrap">
            <FilterBar filters={filters} setFilters={setFilters} stages={stages} categories={categories} t={t} />
            <select value={sortBy} onChange={e => setSortBy(e.target.value)}
              className="px-3 py-1.5 rounded-lg text-xs border"
              style={{ borderColor: 'var(--border-default,#D1D5DB)', color: 'var(--text-secondary,#4B5563)', background: 'var(--bg-input,#fff)' }}>
              <option value="default">{t('meineAufgaben.sort.default', 'Standard')}</option>
              <option value="due_date">{t('meineAufgaben.sort.dueDate', 'Faelligkeitsdatum')}</option>
              <option value="priority">{t('meineAufgaben.sort.priority', 'Prioritaet')}</option>
              <option value="updated_at">{t('meineAufgaben.sort.updated', 'Zuletzt geaendert')}</option>
            </select>
            <label className="flex items-center gap-1.5 text-xs font-medium cursor-pointer" style={{ color: 'var(--text-secondary,#4B5563)' }}>
              <input type="checkbox" checked={showDone} onChange={e => setShowDone(e.target.checked)}
                className="w-3.5 h-3.5 rounded accent-[#338edc]" />
              {t('meineAufgaben.showDone', 'Erledigt zeigen')}
            </label>
            <div className="relative ml-auto">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: 'var(--text-muted,#9CA3AF)' }} />
              <input type="text" value={searchInput} onChange={e => setSearchInput(e.target.value)}
                placeholder={t('meineAufgaben.search', 'Suche...')}
                className="pl-8 pr-3 py-1.5 rounded-lg border text-xs w-48"
                style={{ borderColor: 'var(--border-default,#D1D5DB)', background: 'var(--bg-input,#fff)', color: 'var(--text-primary,#1A1A2E)' }} />
            </div>
          </div>
        </div>
      )}

      {/* Kanban: Category Tabs + Filter + Editor */}
      {activeTab === 'kanban' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-1 bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border overflow-x-auto" style={{ borderColor: 'var(--border-default,#E5E7EB)' }}>
              {categories.map(cat => (
                <button key={cat.id} onClick={() => setSelectedCategoryId(cat.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] text-xs font-medium transition-all whitespace-nowrap ${
                    selectedCategoryId === cat.id ? 'bg-white shadow-sm font-semibold dark:bg-[var(--bg-card)]' : 'hover:text-[var(--text-primary)]'
                  }`}
                  style={{ color: selectedCategoryId === cat.id ? 'var(--text-primary,#1A1A2E)' : 'var(--text-secondary,#4B5563)' }}>
                  <div className="w-2.5 h-2.5 rounded-sm" style={{ background: cat.color || '#338EDC' }} />
                  {cat.name}
                  {cat.task_count > 0 && (
                    <span className="ml-0.5 text-[10px] font-semibold px-1 py-0.5 rounded-full" style={{ background: 'var(--bg-inset,#F3F4F6)', color: 'var(--text-muted,#9CA3AF)' }}>
                      {cat.task_count}
                    </span>
                  )}
                </button>
              ))}
            </div>
            <button onClick={() => setEditorOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border"
              style={{ borderColor: 'var(--border-default,#D1D5DB)', color: 'var(--text-secondary,#4B5563)' }}>
              <Settings className="w-3.5 h-3.5" />
              {t('meineAufgaben.categories.manage', 'Kategorien verwalten')}
            </button>
          </div>
          <FilterBar filters={filters} setFilters={setFilters} stages={stages} categories={categories} t={t} />
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="p-8 text-center" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
          <div className="inline-block w-6 h-6 border-2 border-[#338edc] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {/* INBOX: list with accept button */}
          {activeTab === 'inbox' && (
            <div className="space-y-2">
              {filteredTasks.length === 0 && (
                <div className="bg-[var(--bg-card,#fff)] rounded-xl border p-8 text-center text-sm" style={{ borderColor: 'var(--border-card, #E5E7EB)', color: 'var(--text-tertiary, #6B7280)' }}>
                  {t('meineAufgaben.empty.inbox', 'Keine neuen Aufgaben in der Inbox.')}
                </div>
              )}
              {filteredTasks.map(task => (
                <div key={task.id} className="flex items-center gap-2">
                  <div className="flex-1">
                    <TaskCard task={task} onToggleDone={handleToggleDone} onDelete={handleDelete}
                      onClick={() => setDetailTask(task)} t={t} categories={categories} />
                  </div>
                  {task.status !== 'erledigt' && (
                    <button onClick={(e) => { e.stopPropagation(); setDetailTask(task); }}
                      className="px-2 py-1 rounded-lg text-[11px] font-semibold text-white whitespace-nowrap"
                      style={{ background: '#338edc' }} title={t('meineAufgaben.acceptHint', 'Aufgabe pruefen und annehmen')}>
                      {t('meineAufgaben.accept', 'Annehmen')}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* ALL: universal list */}
          {activeTab === 'all' && (
            <div className="space-y-2">
              <form onSubmit={handleQuickAdd} className="flex items-center gap-2">
                <Plus className="w-4 h-4" style={{ color: 'var(--text-tertiary, #6B7280)' }} />
                <input type="text" value={quickAdd} onChange={e => setQuickAdd(e.target.value)}
                  placeholder={t('meineAufgaben.quickAddAll', 'Neue Aufgabe hinzufuegen...')}
                  className="flex-1 px-3 py-2 rounded-lg border text-sm"
                  style={{ borderColor: 'var(--border-default, #D1D5DB)', background: 'var(--bg-input, #fff)', color: 'var(--text-primary, #1A1A2E)' }} />
                <button type="submit" className="px-3 py-2 rounded-lg text-xs font-semibold text-white" style={{ background: '#338edc' }}>
                  {t('meineAufgaben.add', 'Hinzufuegen')}
                </button>
              </form>
              {filteredTasks.length === 0 && (
                <div className="bg-[var(--bg-card,#fff)] rounded-xl border p-8 text-center text-sm" style={{ borderColor: 'var(--border-card, #E5E7EB)', color: 'var(--text-tertiary, #6B7280)' }}>
                  {t('meineAufgaben.empty.all', 'Keine Aufgaben gefunden.')}
                </div>
              )}
              {filteredTasks.map(task => (
                <TaskCard key={task.id} task={task} onToggleDone={handleToggleDone} onDelete={handleDelete}
                  onClick={() => setDetailTask(task)} t={t} categories={categories}
                  showAssignee={delegation === 'created' || delegation === 'watching'} />
              ))}
            </div>
          )}

          {/* KANBAN VIEW */}
          {activeTab === 'kanban' && (
            <div className="flex gap-3 overflow-x-auto pb-4" style={{ scrollSnapType: 'x mandatory' }}>
              {kanbanData.columns.map(col => (
                <KanbanColumn key={col.id} stage={col}
                  tasks={applyFilters(col.tasks)}
                  onDrop={handleStageDrop} onCardClick={setDetailTask} onToggleDone={handleKanbanToggleDone} t={t} categories={categories} />
              ))}
            </div>
          )}
        </>
      )}

      {/* Create Modal */}
      <CreateModal open={createOpen} onClose={() => setCreateOpen(false)} onSaved={refresh} categories={categories} t={t} />

      {/* Detail Slide-Over */}
      {detailTask && (
        <TaskDetailPanel task={detailTask} onClose={() => setDetailTask(null)} onRefresh={refresh} categories={categories} t={t} onToast={setToast} />
      )}

      {/* Category + Stage Editor */}
      <CategoryStageEditor open={editorOpen} onClose={() => setEditorOpen(false)}
        categories={categories} onRefresh={() => { loadCategories(); loadStages(); loadKanban(); }} t={t} />

      {/* Delete Confirm (main list) */}
      <ConfirmDialog
        open={!!confirmDeleteMain}
        onConfirm={executeDeleteMain}
        onCancel={() => setConfirmDeleteMain(null)}
        title={t('meineAufgaben.deleteTitle', 'Aufgabe loeschen')}
        message={t('meineAufgaben.deleteConfirm', 'Aufgabe wirklich loeschen?')}
        confirmLabel={t('meineAufgaben.delete', 'Loeschen')}
        cancelLabel={t('meineAufgaben.cancel', 'Abbrechen')}
        variant="danger"
      />

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-[100] flex items-center gap-2 px-4 py-3 rounded-xl shadow-sm text-sm font-medium text-white animate-fade-in"
          style={{ background: toast.type === 'error' ? '#EF4444' : '#22C55E' }}>
          {toast.type === 'error' ? <AlertCircle className="w-4 h-4 shrink-0" /> : <CheckCircle2 className="w-4 h-4 shrink-0" />}
          {toast.message}
          <button onClick={() => setToast(null)} className="ml-2 hover:opacity-80"><X className="w-3.5 h-3.5" /></button>
        </div>
      )}
    </div>
  );
}

